import { Phone, Mail, MapPin, MessageCircle, ArrowUpRight } from "lucide-react";
import { PageHero, Reveal } from "../components/Reveal";
import EnquiryForm from "../components/EnquiryForm";
import { CONTACT } from "../data/site";
import useSeo from "../components/Seo";

export default function Quote() {
  useSeo("Request a Quote", "Request a transportation quote — share your pickup and delivery locations, cargo or vehicle type, load and required date. Our team responds with a clear plan.");
  return (
    <>
      <PageHero
        overline="Request a Quote"
        lines={[<>Tell us what</>, <>needs to <em className="italic text-gold-light">move</em></>]}
        desc="Complete the enquiry below and our team will respond with a clear, professional transportation plan. Prefer to talk? Call or WhatsApp us directly."
      />

      <section className="py-24 sm:py-32" data-testid="quote-form-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <Reveal className="lg:col-span-7">
            <div className="border border-gold/15 bg-surface p-8 sm:p-12" data-testid="quote-form-panel">
              <p className="overline-gold">Transportation Enquiry</p>
              <h2 className="mt-4 font-heading text-2xl text-foreground sm:text-3xl">Tell us about the movement</h2>
              <div className="mt-10">
                <EnquiryForm testIdPrefix="quote-form" />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <aside className="border border-gold/20 bg-surface p-10 lg:sticky lg:top-28" data-testid="quote-contact-aside">
              <p className="overline-gold">Direct Channels</p>
              <div className="mt-8 divide-y divide-gold/10 border-y border-gold/10">
                <a href={`tel:${CONTACT.phoneRaw}`} data-testid="quote-call-link" className="group flex items-start gap-4 py-5">
                  <Phone className="mt-1 h-5 w-5 shrink-0 text-gold" />
                  <span>
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">Call Us</span>
                    <span className="mt-1.5 block text-sm text-foreground/90 transition-colors group-hover:text-gold-light">{CONTACT.phoneDisplay}</span>
                  </span>
                </a>
                <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="quote-whatsapp-link" className="group flex items-start gap-4 py-5">
                  <MessageCircle className="mt-1 h-5 w-5 shrink-0 text-gold" />
                  <span>
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">WhatsApp</span>
                    <span className="mt-1.5 block text-sm text-foreground/90 transition-colors group-hover:text-gold-light">Message our team instantly</span>
                  </span>
                </a>
                <a href={`mailto:${CONTACT.email}`} data-testid="quote-email-link" className="group flex items-start gap-4 py-5">
                  <Mail className="mt-1 h-5 w-5 shrink-0 text-gold" />
                  <span>
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">Email</span>
                    <span className="mt-1.5 block break-words text-sm text-foreground/90 transition-colors group-hover:text-gold-light">{CONTACT.email.split("@")[0]}<wbr />@{CONTACT.email.split("@")[1]}</span>
                  </span>
                </a>
                <div className="flex items-start gap-4 py-5" data-testid="quote-office-block">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-gold" />
                  <span>
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-gold">Office</span>
                    <span className="mt-1.5 block text-sm leading-relaxed text-foreground/90">{CONTACT.office}</span>
                  </span>
                </div>
              </div>

              <div className="mt-10" data-testid="quote-next-steps">
                <p className="overline-gold">What Happens Next</p>
                <ol className="mt-6 space-y-5">
                  {[
                    "Our coordination team reviews your enquiry.",
                    "Route feasibility and vehicle allocation are confirmed.",
                    "You receive a clear, written transportation plan.",
                  ].map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="font-heading text-lg leading-none text-gold/70">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm leading-relaxed text-muted-foreground">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noopener noreferrer" className="btn-gold mt-10 w-full" data-testid="quote-whatsapp-cta">
                WhatsApp Us <ArrowUpRight className="h-4 w-4" />
              </a>
            </aside>
          </Reveal>
        </div>
      </section>
    </>
  );
}
